import { apiClient } from './client';
import type {
  LearningAnalysisDto,
  LearningAnalysisViewModel,
  SubjectMasteryDto,
  SubjectMasteryViewModel,
  WeakPointDto,
  WeakPointViewModel,
} from '../types/learningAnalysis';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const LEARNING_ANALYSIS_PATH = '/api/v1/student/learning-analysis';

const fallbackLearningAnalysisDto: LearningAnalysisDto = {
  summary: {
    overall_mastery_percent: 72,
    streak_days: 7,
    mastered_wrong_count: 12,
    total_practice_count: 86,
  },
  subjects: [
    {
      id: 'subject-math',
      subject_name: '数学',
      mastery_percent: 72,
      weak_point: '二次函数最值',
      trend_label: '稳步提升',
    },
    {
      id: 'subject-physics',
      subject_name: '物理',
      mastery_percent: 64,
      weak_point: '浮力计算',
      trend_label: '需要复盘',
    },
    {
      id: 'subject-english',
      subject_name: '英语',
      mastery_percent: 81,
      weak_point: '完形填空语境',
      trend_label: '表现稳定',
    },
  ],
  weak_points: [
    {
      id: 'weak-function',
      title: '二次函数应用',
      subject: '数学',
      priority_label: '高优先级',
      route_key: 'photo-search',
    },
    {
      id: 'weak-friction',
      title: '力学-摩擦力',
      subject: '物理',
      priority_label: '需要复盘',
      route_key: 'photo-search',
    },
  ],
};

const safeNumber = (value: number | null): number => {
  if (value === null || Number.isNaN(value)) {
    return 0;
  }
  return value;
};

const clampPercent = (value: number | null): number =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const mapSubjectMasteryDtoToViewModel = (
  dto: SubjectMasteryDto
): SubjectMasteryViewModel => ({
  id: dto.id,
  subjectName: dto.subject_name ?? '未命名学科',
  masteryPercent: clampPercent(dto.mastery_percent),
  weakPoint: dto.weak_point ?? '暂无薄弱点',
  trendLabel: dto.trend_label ?? '暂无趋势',
});

const mapWeakPointDtoToViewModel = (
  dto: WeakPointDto,
  index: number
): WeakPointViewModel => ({
  id: dto.id || `weak-point-${index + 1}`,
  title: dto.title ?? '未命名薄弱点',
  subject: dto.subject ?? '综合',
  priorityLabel: dto.priority_label ?? '待复盘',
  routeKey: dto.route_key ?? null,
});

export const mapLearningAnalysisDtoToViewModel = (
  dto: LearningAnalysisDto
): LearningAnalysisViewModel => ({
  summary: {
    overallMasteryPercent: clampPercent(dto.summary?.overall_mastery_percent ?? null),
    streakDays: Math.max(0, Math.round(safeNumber(dto.summary?.streak_days ?? null))),
    masteredWrongCount: Math.max(0, Math.round(safeNumber(dto.summary?.mastered_wrong_count ?? null))),
    totalPracticeCount: Math.max(0, Math.round(safeNumber(dto.summary?.total_practice_count ?? null))),
  },
  subjects: (dto.subjects ?? []).map(mapSubjectMasteryDtoToViewModel),
  weakPoints: (dto.weak_points ?? []).map(mapWeakPointDtoToViewModel),
});

export const getLearningAnalysis = async (): Promise<LearningAnalysisViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapLearningAnalysisDtoToViewModel(fallbackLearningAnalysisDto);
  }

  const response = await apiClient.get<LearningAnalysisDto>(LEARNING_ANALYSIS_PATH);
  return mapLearningAnalysisDtoToViewModel(response.data);
};
